/**
 * Inline validators for Spanish identity / tax documents and IBAN.
 *
 * These mirror the canonical Python implementations in
 * `backend/services/document_validation.py` exactly. They exist purely for
 * UX feedback while the user types — the backend is still source of
 * truth and runs the same checks at submit time. Do NOT branch business
 * rules between the two implementations: if a rule changes server-side,
 * update both files.
 *
 * Each validator returns: { valid: boolean, error: string|null,
 * normalized: string }. `valid` is `true` for empty input so optional
 * fields don't show a false error before the user has typed anything.
 */

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIF_LETTER_MAP = "JABCDEFGHI";
const CIF_LETTER_ONLY = new Set(["K", "P", "Q", "S"]);
const CIF_DIGIT_ONLY = new Set(["A", "B", "E", "H"]);

const DNI_RE = /^\d{8}[A-Z]$/;
const NIE_RE = /^[XYZ]\d{7}[A-Z]$/;
const CIF_RE = /^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/;
const PASSPORT_RE = /^[A-Z0-9]{5,20}$/;
const IBAN_BASE_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

// IBAN Registry 2024-11 — same table as the backend.
const IBAN_LENGTHS = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22,
  BR: 29, BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28,
  EE: 20, ES: 24, FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18,
  GR: 27, GT: 28, HR: 21, HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30,
  KW: 30, KZ: 20, LB: 28, LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24,
  ME: 22, MK: 19, MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28,
  PS: 29, PT: 25, QA: 29, RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24,
  SM: 27, TN: 24, TR: 26, UA: 29, VG: 24, XK: 20,
};

export const normalize = (raw) => {
  if (!raw) return "";
  return String(raw).trim().toUpperCase().replace(/[\s-]+/g, "");
};

const dniControl = (digits) => DNI_LETTERS[parseInt(digits, 10) % 23];

const ok = (normalized) => ({ valid: true, error: null, normalized });
const ko = (msg, normalized) => ({ valid: false, error: msg, normalized });

export const validateDni = (value) => {
  const v = normalize(value);
  if (!v) return ok(v);
  if (!DNI_RE.test(v)) return ko("Formato DNI inválido (8 dígitos + letra).", v);
  if (dniControl(v.slice(0, 8)) !== v[8]) return ko("DNI: letra de control incorrecta.", v);
  return ok(v);
};

export const validateNie = (value) => {
  const v = normalize(value);
  if (!v) return ok(v);
  if (!NIE_RE.test(v)) return ko("Formato NIE inválido (X/Y/Z + 7 dígitos + letra).", v);
  const digits = ({ X: "0", Y: "1", Z: "2" }[v[0]]) + v.slice(1, 8);
  if (dniControl(digits) !== v[8]) return ko("NIE: letra de control incorrecta.", v);
  return ok(v);
};

export const validateCif = (value) => {
  const v = normalize(value);
  if (!v) return ok(v);
  if (!CIF_RE.test(v)) return ko("Formato CIF/NIF inválido (1 letra + 7 dígitos + control).", v);
  const letter = v[0];
  const digits = v.slice(1, 8);
  const control = v[8];
  let oddSum = 0;
  for (let i = 0; i < 7; i += 2) {
    const d = parseInt(digits[i], 10) * 2;
    oddSum += Math.floor(d / 10) + (d % 10);
  }
  let evenSum = 0;
  for (let i = 1; i < 7; i += 2) evenSum += parseInt(digits[i], 10);
  const total = oddSum + evenSum;
  const ctrlDigit = (10 - (total % 10)) % 10;
  const ctrlLetter = CIF_LETTER_MAP[ctrlDigit];
  if (CIF_LETTER_ONLY.has(letter)) {
    return control === ctrlLetter ? ok(v) : ko("CIF/NIF: carácter de control incorrecto.", v);
  }
  if (CIF_DIGIT_ONLY.has(letter)) {
    return control === String(ctrlDigit) ? ok(v) : ko("CIF/NIF: dígito de control incorrecto.", v);
  }
  if (control === String(ctrlDigit) || control === ctrlLetter) return ok(v);
  return ko("CIF/NIF: carácter de control incorrecto.", v);
};

export const validatePassport = (value) => {
  const v = normalize(value);
  if (!v) return ok(v);
  if (!PASSPORT_RE.test(v)) return ko("Pasaporte: 5–20 caracteres alfanuméricos.", v);
  return ok(v);
};

export const validateDocument = (customerType, documentType, value) => {
  if (customerType === "EMPRESA") return validateCif(value);
  const dt = (documentType || "DNI").toUpperCase();
  if (dt === "DNI") return validateDni(value);
  if (dt === "NIE") return validateNie(value);
  if (dt === "PASAPORTE" || dt === "PASSPORT") return validatePassport(value);
  return ko(`Tipo de documento desconocido: ${dt}`, normalize(value));
};

export const validateIban = (value) => {
  const v = normalize(value);
  if (!v) return ok(v);
  if (!IBAN_BASE_RE.test(v)) {
    return ko("IBAN: 2 letras + 2 dígitos + cuenta (p. ej. ES00 0000 0000 0000 0000 0000).", v);
  }
  const country = v.slice(0, 2);
  const expected = IBAN_LENGTHS[country];
  if (expected != null && v.length !== expected) {
    return ko(`IBAN: longitud para ${country} debe ser ${expected}.`, v);
  }
  if (expected == null && (v.length < 15 || v.length > 34)) {
    return ko("IBAN: longitud entre 15 y 34 caracteres.", v);
  }
  // mod-97 check
  const rearranged = v.slice(4) + v.slice(0, 4);
  let numeric = "";
  for (const c of rearranged) {
    numeric += /[A-Z]/.test(c) ? String(c.charCodeAt(0) - 55) : c;
  }
  // BigInt mod 97 (numeric strings can exceed 2^53)
  let remainder = 0;
  for (const ch of numeric) {
    remainder = (remainder * 10 + parseInt(ch, 10)) % 97;
  }
  if (remainder !== 1) return ko("IBAN: dígito de control incorrecto.", v);
  return ok(v);
};


// ── Email & phone ────────────────────────────────────────────────────────
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const validateEmail = (value) => {
  if (!value) return ok("");
  const v = String(value).trim();
  if (!EMAIL_RE.test(v)) return ko("Email inválido. Formato: usuario@dominio.tld", v);
  return ok(v);
};

// Practical, country-aware phone digit-length rules. Mirrors the backend
// `PHONE_RULES` table; missing countries fall through to DEFAULT_PHONE_RANGE.
export const PHONE_RULES = {
  ES: [9, 9], PT: [9, 9], FR: [9, 10], IT: [6, 11], DE: [6, 13],
  GB: [9, 11], IE: [7, 9], NL: [9, 9], BE: [8, 9], LU: [8, 9],
  AT: [10, 13], CH: [9, 9], PL: [9, 9], CZ: [9, 9], SK: [9, 9],
  HU: [9, 9], RO: [9, 9], BG: [8, 9], GR: [10, 10], DK: [8, 8],
  NO: [8, 8], SE: [7, 13], FI: [5, 12], IS: [7, 7], EE: [7, 8],
  LV: [8, 8], LT: [8, 8], HR: [8, 9], SI: [8, 8], MT: [8, 8],
  CY: [8, 8], TR: [10, 10], US: [10, 10], BR: [10, 11],
  AE: [8, 9], SA: [9, 9], IL: [9, 9],
};
const DEFAULT_PHONE_RANGE = [7, 15];

export const validatePhone = (value, country = "ES") => {
  if (!value) return ok("");
  const digits = String(value).replace(/\D+/g, "").replace(/^0+/, "");
  if (!digits) return ko("Teléfono inválido.", String(value));
  const cc = (country || "ES").toUpperCase();
  const [lo, hi] = PHONE_RULES[cc] || DEFAULT_PHONE_RANGE;
  if (digits.length < lo || digits.length > hi) {
    const rangeMsg = lo === hi ? `${lo} dígitos` : `entre ${lo} y ${hi} dígitos`;
    return ko(`Teléfono inválido para ${cc}: debe tener ${rangeMsg}.`, String(value).trim());
  }
  return ok(String(value).trim());
};

// Re-exposed for UI consumers (e.g. country selector dropdowns) so we keep
// a single source of truth for the supported country list.
export const SUPPORTED_COUNTRIES = Object.keys(IBAN_LENGTHS).sort();
